import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { Moment } from 'moment';
import { SharedModule } from '@shared/shared.module';
import { PlatformBirthInformationRequest } from '@data/_generated-api/platform-api/models/platform-birth-information-request';
import { PlatformCalculateChartRequest } from '@data/_generated-api/platform-api/models/platform-calculate-chart-request';
import { HdChartLibraryService } from './hd-chart-library.service';
import { HdChartGroupService } from './hd-chart-group.service';
import { HdBirthLocation, HdBirthTimeValue, HdChartCalculationResult, HdChartFormInitialValue } from './hd-chart-library.models';
import { formatBirthDate, toIsoUtc } from './hd-chart-create.utils';
import { birthInputsUnchanged } from './hd-chart-edit.utils';
import { HdBirthTimeInputComponent } from './inputs/hd-birth-time-input.component';
import { HdBirthDateInputComponent } from './inputs/hd-birth-date-input.component';
import { HdLocationPickerComponent } from './inputs/hd-location-picker.component';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

export function buildBirthRequest(name: string, date: Moment, time: string, loc: HdBirthLocation): PlatformBirthInformationRequest {
  return {
    name, birthDate: formatBirthDate(date), birthTime: time, birthPlace: loc.place,
    country: loc.countryCode || null,
    latitude: loc.latitude, longitude: loc.longitude, timezone: loc.timezone,
  };
}

/** UTC-invoer is geocentrisch compleet: zonder locatie rekent de backend gewoon (alleen de angles vervallen). */
export function buildUtcRequest(name: string, date: Moment, time: string, loc: HdBirthLocation | null): PlatformCalculateChartRequest {
  return {
    name, utc: toIsoUtc(date, time), timezone: loc?.timezone || 'UTC',
    location: loc ? { city: loc.city, country: loc.countryCode, lat: loc.latitude, lon: loc.longitude } : null,
  };
}

@Component({
  selector: 'hd-chart-create-form',
  standalone: true,
  imports: [SharedModule, TranslocoModule, HdBirthTimeInputComponent, HdBirthDateInputComponent, HdLocationPickerComponent],
  templateUrl: './hd-chart-create-form.component.html',
  styleUrl: './hd-chart-create-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartCreateFormComponent {
  private readonly library = inject(HdChartLibraryService);
  private readonly groupService = inject(HdChartGroupService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  /** Voorinvulling (edit-modus): aanwezig → formulier bewerkt een bestaande chart. */
  readonly initial = input<HdChartFormInitialValue | undefined>(undefined);
  /**
   * Edit-modus voor cycle-/composite-charts: enkel de naam is bewerkbaar.
   * Een herberekening zou het cycle-/composite-document door een natal chart
   * vervangen; de geboortevelden en het herbereken-vinkje verdwijnen dus.
   */
  readonly renameOnly = input(false);
  /** false: de host kan geen onopgeslagen chart tonen → vinkje "enkel berekenen" verbergen (blijft dan uit). */
  readonly calculateOnlyOffered = input(true);
  readonly calculated = output<HdChartCalculationResult>();
  /** Edit-modus: geboortegegevens onaangeroerd → enkel de (nieuwe) naam, zonder herberekening. */
  readonly renamedOnly = output<string>();
  readonly cancelled = output<void>();

  protected readonly name = signal('');
  protected readonly date = signal<Moment | undefined>(undefined);
  protected readonly timeValue = signal<HdBirthTimeValue>({ time: '12:00', isUtc: false, display: '24h' });
  protected readonly location = signal<HdBirthLocation | null>(null);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  /** Edit-modus: gebruiker wil herberekenen ondanks onaangeroerde geboorte-invoer. */
  protected readonly forceRecalculate = signal(false);
  /** Create-modus: "enkel berekenen" aangevinkt → de pagina slaat niet automatisch op. */
  protected readonly calculateOnly = signal(false);
  /**
   * Create-modus: zolang er nog geen "mijn chart" bestaat bieden we het vinkje
   * "Markeer als mijn chart" aan (feedback 2026-08-23) — een nieuwe gebruiker
   * maakt vrijwel altijd eerst zijn eigen chart, dus standaard aangevinkt.
   */
  protected readonly markAsMyChartOffered = signal(false);
  protected readonly markAsMyChart = signal(true);

  /** Edit-modus: wijkt de huidige geboorte-invoer af van de voorinvulling? */
  protected readonly birthChanged = computed(() => {
    const initial = this.initial();
    if (!initial) return false;
    return !birthInputsUnchanged(initial, { date: this.date(), time: this.timeValue(), location: this.location() });
  });

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      nameLabel: translate('humanDesign.chart.createDialog.nameLabel'),
      namePlaceholder: translate('humanDesign.chart.createDialog.namePlaceholder'),
      nameAria: translate('humanDesign.chart.createDialog.nameAria'),
      birthDateLabel: translate('humanDesign.chart.createDialog.birthDateLabel'),
      birthTimeLabel: translate('humanDesign.chart.createDialog.birthTimeLabel'),
      locationLabel: translate('humanDesign.chart.createDialog.locationLabel'),
      locationOptionalHint: translate('humanDesign.chart.createDialog.locationOptionalHint'),
      cancel: translate('humanDesign.chart.createDialog.cancel'),
      calculate: translate('humanDesign.chart.createDialog.calculate'),
      calculating: translate('humanDesign.chart.createDialog.calculating'),
      calculationError: translate('humanDesign.chart.createDialog.calculationError'),
      calculateOnlyLabel: translate('humanDesign.chart.createDialog.calculateOnlyLabel'),
      markAsMyChartLabel: translate('humanDesign.chart.createDialog.markAsMyChartLabel'),
      submit: this.initial()
        ? translate('humanDesign.chart.editDialog.save')
        : this.calculateOnly()
          ? translate('humanDesign.chart.createDialog.calculate')
          : translate('humanDesign.chart.createDialog.calculateAndSave'),
      recalculate: translate('humanDesign.chart.editDialog.recalculate'),
      renameOnlyHint: translate('humanDesign.chart.editDialog.renameOnlyHint'),
    };
  });

  ngOnInit(): void {
    const initial = this.initial();
    if (!initial) {
      void this.checkMarkAsMyChartOffer();
      return;
    }
    this.name.set(initial.name);
    this.date.set(initial.date);
    this.timeValue.set(initial.time);
    this.location.set(initial.location);
  }

  /** Het vinkje enkel tonen wanneer nog géén chart als "mijn chart" gemarkeerd is. */
  private async checkMarkAsMyChartOffer(): Promise<void> {
    try {
      const myChart = await this.groupService.getMyChart();
      this.markAsMyChartOffered.set(myChart == null);
    } catch {
      this.markAsMyChartOffered.set(false);
    }
  }

  protected onDateChange(value: Moment | undefined): void { this.date.set(value); }
  protected onTimeChange(value: HdBirthTimeValue): void { this.timeValue.set(value); }
  protected onLocationChange(value: HdBirthLocation | null): void { this.location.set(value); }
  protected onForceRecalculateChange(checked: boolean): void { this.forceRecalculate.set(checked); }

  protected get canSubmit(): boolean {
    if (this.loading() || this.name().trim().length === 0) return false;
    // Naam-only wijziging kan ook wanneer de voorinvulling onvolledig was (bv. geen locatie).
    if (this.isRenameOnly()) return true;
    if (this.date() == null) return false;
    // UTC-invoer: locatie optioneel — het moment is al exact, de chart wordt dan
    // zonder coördinaten bewaard (zelfde model als geïmporteerde GMT-charts).
    return this.timeValue().isUtc || this.location() != null;
  }

  /** Edit-modus, vergrendeld (cycle/composite) óf geboorte-invoer onaangeroerd zonder geforceerde herberekening → enkel hernoemen. */
  private isRenameOnly(): boolean {
    if (!this.initial()) return false;
    return this.renameOnly() || (!this.birthChanged() && !this.forceRecalculate());
  }

  async submit(): Promise<void> {
    const name = this.name().trim();
    if (!name || !this.canSubmit) return;
    if (this.isRenameOnly()) {
      this.renamedOnly.emit(name);
      return;
    }
    const date = this.date(); const loc = this.location();
    const time = this.timeValue();
    if (!date || (!loc && !time.isUtc)) return;
    this.loading.set(true);
    this.error.set(null);
    try {
      const preset = time.isUtc
        ? await this.library.calculateFromUtc(buildUtcRequest(name, date, time.time, loc))
        : await this.library.calculateFromBirth(buildBirthRequest(name, date, time.time, loc!));
      this.calculated.emit({
        preset,
        name,
        calculateOnly: this.calculateOnly(),
        // Markeren vereist een opgeslagen chart: met "enkel berekenen" vervalt het.
        ...(this.markAsMyChartOffered()
          ? { markAsMyChart: this.markAsMyChart() && !this.calculateOnly() }
          : {}),
      });
    } catch (e) {
      this.error.set(
        e instanceof Error
          ? e.message
          : this.text().calculationError,
      );
    } finally {
      this.loading.set(false);
    }
  }
}
